import { useMemo, useState } from "react";
import { format, addDays, startOfDay } from "date-fns";
import { enGB } from "date-fns/locale";
import { Loader2, Calendar as CalendarIcon, Clock, Video, MapPin } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

import { ResponsiveDialog } from "@/components/ui/responsive-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { useAvailability, useBookingSettings, type AvailabilitySlot } from "@/hooks/useAvailability";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface BookSessionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onBooked?: (sessionId: string) => void;
}

type Step = 1 | 2 | 3 | 4;
type Location = "online" | "office";

export function BookSessionDialog({ open, onOpenChange, onBooked }: BookSessionDialogProps) {
  const [step, setStep] = useState<Step>(1);
  const [duration, setDuration] = useState<number | null>(null);
  const [location, setLocation] = useState<Location | null>(null);
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<AvailabilitySlot | null>(null);
  const [title, setTitle] = useState("");
  const [focus, setFocus] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const qc = useQueryClient();
  const { data: settings } = useBookingSettings();
  const slotLengths: number[] = (settings?.slot_lengths as number[]) || [30, 45, 60];
  const horizonDays = settings?.horizon_days ?? 28;
  const coachTz: string = settings?.timezone || "Europe/Zurich";
  const onlineEnabled = settings?.online_enabled !== false;
  const officeEnabled = settings?.office_enabled !== false;
  const officeAddress: string | null = (settings as any)?.office_address || null;

  // Format a Date as yyyy-MM-dd in the coach's timezone, keeps day buckets
  // aligned with Phil's calendar regardless of the client's browser timezone.
  const tzDayKey = (d: Date) => {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: coachTz, year: "numeric", month: "2-digit", day: "2-digit",
    }).formatToParts(d);
    const map: Record<string, string> = {};
    for (const p of parts) map[p.type] = p.value;
    return `${map.year}-${map.month}-${map.day}`;
  };
  const dayLabelInTz = (key: string) => {
    const [y, m, d] = key.split("-").map(Number);
    // Build a Date that lands at noon coach-tz to avoid DST edges
    const noonGuess = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
    return noonGuess;
  };

  // Fetch availability across the full horizon once a duration is picked.
  const { from, to } = useMemo(() => {
    const f = startOfDay(new Date());
    const t = addDays(f, horizonDays);
    return { from: f, to: t };
  }, [horizonDays]);

  const { data: availability, isLoading: loadingAvailability } = useAvailability({
    durationMinutes: duration || 60,
    from,
    to,
    enabled: !!duration && !!location && step >= 3,
    location: location || "online",
  });

  // Group slots by local day (yyyy-MM-dd), excluding weekends as a safety net.
  const { availableDays, slotsByDay, availableDayKeys } = useMemo(() => {
    const map = new Map<string, AvailabilitySlot[]>();
    const slots = availability?.slots ?? [];
    for (const s of slots) {
      const d = new Date(s.start);
      const key = tzDayKey(d);
      const arr = map.get(key) ?? [];
      arr.push(s);
      map.set(key, arr);
    }
    const days = Array.from(map.keys()).sort().map((k) => dayLabelInTz(k));
    return { availableDays: days, slotsByDay: map, availableDayKeys: new Set(map.keys()) };
  }, [availability, coachTz]);

  const slotsForSelectedDay: AvailabilitySlot[] = useMemo(() => {
    if (!selectedDay) return [];
    return slotsByDay.get(tzDayKey(selectedDay)) ?? [];
  }, [selectedDay, slotsByDay]);

  const reset = () => {
    setStep(1);
    setDuration(null);
    setLocation(null);
    setSelectedDay(null);
    setSelectedSlot(null);
    setTitle("");
    setFocus("");
    setSubmitting(false);
  };

  const handleClose = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const confirmBooking = async () => {
    if (!selectedSlot || !duration || !location) return;
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-booking", {
        body: {
          start: selectedSlot.start,
          duration_minutes: duration,
          title: title.trim() || "Coaching Session",
          focus: focus.trim() || null,
          location,
        },
      });
      // supabase.functions.invoke returns { error } for non-2xx; the body
      // is still in `data` when the function returned JSON.
      const payload = (data ?? {}) as { error?: string; warning?: string; session_id?: string };
      const errMsg = payload?.error || error?.message;
      const status = (error as { context?: { status?: number } } | undefined)?.context?.status;

      if (errMsg) {
        if (status === 409 || /no longer available/i.test(errMsg)) {
          toast({
            title: "That slot was just taken",
            description: "Please pick another time.",
            variant: "destructive",
          });
          // Refresh availability and bounce to day picker
          await qc.invalidateQueries({ queryKey: ["availability"] });
          setSelectedSlot(null);
          setStep(3);
          return;
        }
        throw new Error(errMsg);
      }
      toast({
        title: "Session booked",
        description: payload.warning || "Your session is confirmed.",
        variant: payload.warning ? "default" : "default",
      });
      qc.invalidateQueries({ queryKey: ["sessions"] });
      qc.invalidateQueries({ queryKey: ["availability"] });
      onBooked?.(payload.session_id);
      handleClose(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Could not book this slot.";
      toast({ title: "Booking failed", description: msg, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={handleClose}
      title="Book a Session"
      description={`Step ${step} of 4`}
    >
      <div className="space-y-5 py-2">
        {step === 1 && (
          <div className="space-y-3">
            <Label>Pick a session length</Label>
            <div className="grid grid-cols-3 gap-2">
              {slotLengths.map((m) => (
                <Button
                  key={m}
                  variant={duration === m ? "default" : "outline"}
                  onClick={() => setDuration(m)}
                >
                  {m} min
                </Button>
              ))}
            </div>
            <div className="flex justify-end">
              <Button
                disabled={!duration}
                onClick={() => {
                  // Auto-skip the location step if only one option is enabled
                  if (onlineEnabled && !officeEnabled) {
                    setLocation("online");
                    setStep(3);
                  } else if (officeEnabled && !onlineEnabled) {
                    setLocation("office");
                    setStep(3);
                  } else {
                    setStep(2);
                  }
                }}
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <Label>How would you like to meet?</Label>
            <div className="grid gap-2">
              {onlineEnabled && (
                <button
                  type="button"
                  onClick={() => setLocation("online")}
                  className={cn(
                    "rounded-lg border px-4 py-3 text-left transition-colors flex items-start gap-3",
                    location === "online" ? "border-primary bg-primary/5" : "hover:bg-accent",
                  )}
                >
                  <Video className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <div>
                    <div className="font-medium">Online</div>
                    <div className="text-xs text-muted-foreground">
                      Meet in the Conversation Studio.
                    </div>
                  </div>
                </button>
              )}
              {officeEnabled && (
                <button
                  type="button"
                  onClick={() => setLocation("office")}
                  className={cn(
                    "rounded-lg border px-4 py-3 text-left transition-colors flex items-start gap-3",
                    location === "office" ? "border-primary bg-primary/5" : "hover:bg-accent",
                  )}
                >
                  <MapPin className="h-5 w-5 text-brand-gold mt-0.5 shrink-0" />
                  <div>
                    <div className="font-medium">In office</div>
                    <div className="text-xs text-muted-foreground">
                      {officeAddress
                        ? `Meet at ${officeAddress}.`
                        : "Meet at Phil's office."}{" "}
                      Available on selected weekdays only.
                    </div>
                  </div>
                </button>
              )}
            </div>
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep(1)}>Back</Button>
              <Button
                disabled={!location}
                onClick={() => {
                  setSelectedDay(null);
                  setSelectedSlot(null);
                  setStep(3);
                }}
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4" /> Pick a day &amp; time
              <span className="ml-auto text-xs font-normal text-muted-foreground">
                {location === "office" ? "In office" : "Online"}
              </span>
            </Label>
            {loadingAvailability ? (
              <div className="flex items-center justify-center py-8 gap-2 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="text-sm">Finding available days…</span>
              </div>
            ) : availability?.disabled ? (
              <p className="text-sm text-muted-foreground py-4">
                Self-booking is currently paused. Please message Phil to schedule a session.
              </p>
            ) : availableDays.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">
                No {location === "office" ? "in-office" : "online"} availability in the next {horizonDays} days.
                {onlineEnabled && officeEnabled ? " Try the other option, or contact Phil directly." : " Please contact Phil directly."}
              </p>
            ) : (
              <>
              <div className="flex justify-center">
                <Calendar
                  mode="single"
                  weekStartsOn={1}
                  selected={selectedDay ?? undefined}
                  onSelect={(d) => {
                    if (!d) return;
                    setSelectedDay(d);
                    setSelectedSlot(null);
                  }}
                  fromDate={from}
                  toDate={to}
                  defaultMonth={availableDays[0] ?? from}
                  disabled={(date) => !availableDayKeys.has(tzDayKey(date))}
                  modifiers={{
                    available: (date) => availableDayKeys.has(tzDayKey(date)),
                  }}
                  modifiersClassNames={{
                    available: "bg-primary/10 text-primary font-semibold hover:bg-primary/20",
                  }}
                  className={cn("p-4 pointer-events-auto rounded-md border w-full")}
                  classNames={{
                    months: "flex flex-col w-full",
                    month: "space-y-4 w-full",
                    table: "w-full border-collapse",
                    head_row: "flex w-full justify-between",
                    head_cell: "text-muted-foreground rounded-md w-11 font-normal text-[0.8rem]",
                    row: "flex w-full mt-2 justify-between",
                    cell: "h-11 w-11 text-center text-sm p-0 relative focus-within:relative focus-within:z-20",
                    day: cn("h-11 w-11 p-0 font-normal aria-selected:opacity-100 rounded-md hover:bg-accent"),
                    day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground rounded-md",
                  }}
                />
              </div>

              <div className="space-y-2 pt-2 border-t">
                {!selectedDay ? (
                  <p className="text-sm text-muted-foreground py-2 text-center">
                    Select a day to see available times.
                  </p>
                ) : (
                  <>
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <Clock className="h-4 w-4" />
                      {format(selectedDay, "EEE dd/MM/yyyy", { locale: enGB })}
                    </div>
                    {slotsForSelectedDay.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-2">
                        No slots on this day. Try another date.
                      </p>
                    ) : (
                      <div className="grid grid-cols-3 gap-2 max-h-[40vh] overflow-y-auto">
                        {slotsForSelectedDay.map((s) => {
                          const startD = new Date(s.start);
                          const isSel = selectedSlot?.start === s.start;
                          return (
                            <Button
                              key={s.start}
                              variant={isSel ? "default" : "outline"}
                              size="sm"
                              onClick={() => setSelectedSlot(s)}
                            >
                              {format(startD, "HH:mm")}
                            </Button>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
                {availability?.timezone && (
                  <p className="text-xs text-muted-foreground">
                    Times shown in your local time. Coach timezone: {availability.timezone}.
                  </p>
                )}
              </div>
              </>
            )}
            <div className="flex justify-between">
              <Button
                variant="ghost"
                onClick={() =>
                  setStep(onlineEnabled && officeEnabled ? 2 : 1)
                }
              >
                Back
              </Button>
              <Button disabled={!selectedSlot} onClick={() => setStep(4)}>Next</Button>
            </div>
          </div>
        )}

        {step === 4 && selectedSlot && (
          <div className="space-y-3">
            <div className="rounded-lg bg-muted p-3 text-sm">
              <div className="font-medium">
                {format(new Date(selectedSlot.start), "EEE dd/MM/yyyy 'at' HH:mm", { locale: enGB })}
              </div>
              <div className="text-muted-foreground">{duration} minutes with Phil</div>
              <div className="text-muted-foreground flex items-center gap-1.5 mt-1">
                {location === "office" ? (
                  <>
                    <MapPin className="h-3.5 w-3.5" />
                    In office{officeAddress ? `, ${officeAddress}` : ""}
                  </>
                ) : (
                  <>
                    <Video className="h-3.5 w-3.5" />
                    Online (Conversation Studio)
                  </>
                )}
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="booking-title">Title (optional)</Label>
              <Input
                id="booking-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Pitch practice"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="booking-focus">What would you like to focus on? (optional)</Label>
              <Textarea
                id="booking-focus"
                value={focus}
                onChange={(e) => setFocus(e.target.value)}
                rows={3}
              />
            </div>
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep(3)} disabled={submitting}>Back</Button>
              <Button onClick={confirmBooking} disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Confirm booking
              </Button>
            </div>
          </div>
        )}
      </div>
    </ResponsiveDialog>
  );
}